/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import {
  Box, Drawer, Typography, InputAdornment, TextField,
  Card, Chip, IconButton, Tooltip, CircularProgress,
} from '@mui/material';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { usePharmacy } from '../../contexts/PharmacyContext';

const TEAL = '#00798E';

export default function PharmacySelectorRibbon() {
  const { pharmacies, selectedPharmacy, loading, selectPharmacy } = usePharmacy();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = pharmacies.filter((p) =>
    `${p.name} ${p.code}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      {/* Fixed ribbon tab on right edge */}
      <Tooltip title={selectedPharmacy ? `Pharmacy: ${selectedPharmacy.name}` : 'Select Pharmacy'} placement="left">
        <Box
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1300,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: selectedPharmacy ? TEAL : '#e53935',
            color: 'white',
            borderRadius: '8px 0 0 8px',
            px: 0.75,
            py: 1.5,
            gap: 0.5,
            boxShadow: '-2px 2px 12px rgba(0,0,0,0.18)',
            transition: 'all 0.2s',
            '&:hover': { px: 1.25, bgcolor: selectedPharmacy ? '#005e6e' : '#c62828' },
          }}
        >
          {loading ? (
            <CircularProgress size={16} sx={{ color: 'white' }} />
          ) : (
            <LocalPharmacyIcon sx={{ fontSize: 20 }} />
          )}
          <Typography
            sx={{
              fontSize: '0.55rem', fontWeight: 800, letterSpacing: '0.04em',
              writingMode: 'vertical-rl', textOrientation: 'mixed',
              transform: 'rotate(180deg)', maxHeight: 100,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {selectedPharmacy ? selectedPharmacy.code : 'SELECT'}
          </Typography>
        </Box>
      </Tooltip>

      {/* Selection Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: 320, bgcolor: '#f8f9fa' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 2, bgcolor: TEAL, color: 'white' }}>
          <LocalPharmacyIcon sx={{ mr: 1 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', flexGrow: 1 }}>Select Pharmacy</Typography>
          <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ px: 2, py: 1.5 }}>
          <TextField
            size="small" fullWidth
            placeholder="Search pharmacy..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: '#bbb' }} />
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { height: 36, bgcolor: 'white' } }}
          />
        </Box>

        <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.25, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <Typography sx={{ textAlign: 'center', color: '#aaa', fontSize: '0.82rem', py: 4 }}>
              No pharmacies found
            </Typography>
          ) : filtered.map((p) => {
            const isSelected = selectedPharmacy?.id === p.id;
            return (
              <Card
                key={p.id}
                onClick={() => { selectPharmacy(p); setOpen(false); }}
                sx={{
                  p: 1.75, cursor: 'pointer',
                  border: isSelected ? `2px solid ${TEAL}` : '2px solid transparent',
                  bgcolor: isSelected ? '#e8f5f7' : 'white',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: TEAL, bgcolor: '#f0fafb' },
                  boxShadow: isSelected ? `0 0 0 1px ${TEAL}20` : '0 1px 4px rgba(0,0,0,0.06)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box
                    sx={{
                      width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
                      bgcolor: isSelected ? TEAL : '#e8edf0',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <LocalPharmacyIcon sx={{ fontSize: 18, color: isSelected ? 'white' : '#8fa5ae' }} />
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: isSelected ? TEAL : '#222', lineHeight: 1.2 }}>
                      {p.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#888', fontFamily: 'monospace', mt: 0.3 }}>
                      {p.code}
                    </Typography>
                    {p.city && (
                      <Typography sx={{ fontSize: '0.68rem', color: '#aaa', mt: 0.2 }}>{p.city}</Typography>
                    )}
                  </Box>
                  {isSelected && <CheckCircleIcon sx={{ fontSize: 18, color: TEAL, flexShrink: 0, mt: 0.25 }} />}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75 }}>
                  <Chip
                    label={p.is_active === 'Y' ? 'Active' : 'Inactive'}
                    size="small"
                    color={p.is_active === 'Y' ? 'success' : 'default'}
                    sx={{ fontSize: '0.58rem', height: 16, fontWeight: 700 }}
                  />
                  {p.is_default === 'Y' && (
                    <Chip label="Default" size="small" color="info" sx={{ fontSize: '0.58rem', height: 16, fontWeight: 700 }} />
                  )}
                </Box>
              </Card>
            );
          })}
        </Box>
      </Drawer>
    </>
  );
}
